import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';

/**
 * One part requirement on a ticket, flattened from the SN `part_requirements[]` array (see
 * `SNPartRequirement`). Read-only display data — the FE acts on parts through the ticket-action
 * mutations, not by editing these. The booleans (`returnDefective`, `sourced`, `delivered`) are
 * normalised from SN's `{value:"1"|"0", display_value:"true"|"false"}` wrapper to real bools.
 */
@Entity()
export class ServiceNowTicketPart extends AdornisEntity {
  static override _class = 'ServiceNowTicketPart';

  // Required for the baseql client to construct `parts[]` as an array of entities — without a
  // resolvable key it throws "Refusing to merge array of entities without unique IDs". These rows
  // are read-only and replaced wholesale on every ticket fetch, so a fresh per-construction id is
  // fine (same pattern as FieldExtractionKV). No stable identity to preserve across refetches.
  static override resolveID(_entity: ServiceNowTicketPart): string {
    return A.getGloballyUniqueID();
  }

  /** Human label for the part — SN `short_description`, falling back to the model number. */
  @Field(() => String)
  description: Maybe<string>;

  @Field(() => String)
  modelNumber: Maybe<string>;

  @Field(() => String)
  manufacturer: Maybe<string>;

  /** Required quantity for the job (SN `required_quantity`). */
  @Field(() => String)
  quantity: Maybe<string>;

  /** Free-form fulfilment stage from SN (often empty); the UI prefers the sourced/delivered flags. */
  @Field(() => String)
  stage: Maybe<string>;

  /** The part has been sourced/allocated from the stockroom. */
  @Field(() => Boolean)
  sourced: Maybe<boolean>;

  /** The part has been delivered to the FE / site. */
  @Field(() => Boolean)
  delivered: Maybe<boolean>;

  @Field(() => Boolean)
  returnDefective: Maybe<boolean>;

  /** Stockroom / depot the part is sourced from (SN `asset.location`). */
  @Field(() => String)
  assetLocation: Maybe<string>;

  /** Purchase-order reference for the part (SN `asset.po_number`). */
  @Field(() => String)
  poNumber: Maybe<string>;
}

/**
 * One work note on a ticket, as shown in the colleague view's Notes block.
 *
 * `html` is already sanitized server-side (see `sanitizeNoteHtml` in api/colleague-ticket-queries.ts)
 * because ServiceNow mixes markup into note text and the client renders it with `unsafeHTML`.
 */
@Entity()
export class ServiceNowTicketNote extends AdornisEntity {
  static override _class = 'ServiceNowTicketNote';

  // Same rationale as ServiceNowTicketPart: baseql needs a resolvable id to merge an array of
  // entities, and these rows are read-only and replaced wholesale on every fetch.
  static override resolveID(_entity: ServiceNowTicketNote): string {
    return A.getGloballyUniqueID();
  }

  /** SN's raw `yyyy-mm-dd hh:mm:ss`. Formatted for display on the client. */
  @Field(() => String)
  date: Maybe<string>;

  @Field(() => String)
  html: Maybe<string>;
}

@Entity()
export class ServiceNowTicket extends AdornisEntity {
  static override _class = 'ServiceNowTicket';

  @Field(() => String)
  number: Maybe<string>;

  @Field(() => String)
  title: Maybe<string>;

  @Field(() => String)
  description: Maybe<string>;

  @Field(() => String)
  state: Maybe<string>;

  /** Categorical ETA bucket (early_morning|morning|midday|afternoon|evening), read from SN ticket_details. */
  @Field(() => String)
  etaWindow: Maybe<string>;

  @Field(() => String)
  assignedToSysId: Maybe<string>;

  @Field(() => String)
  assignedToName: Maybe<string>;

  @Field(() => String)
  stateLabel: Maybe<string>;

  /** WOT in-flight sub-status while `state` stays put — notably On Route (8) under Accepted (17). */
  @Field(() => String)
  substate: Maybe<string>;

  @Field(() => String)
  substateLabel: Maybe<string>;

  @Field(() => String)
  locationLabel: Maybe<string>;

  @Field(() => String)
  expectedStart: Maybe<string>;

  @Field(() => String)
  expectedStartDisplay: Maybe<string>;

  @Field(() => Float)
  locationLat: Maybe<number>;

  @Field(() => Float)
  locationLng: Maybe<number>;

  // Client/contact fields, mapped from the SN `customer`/`contact` objects on ticket_details
  // (and ticket_list). `clientSysId` (customer.reference sys_id) also drives customer access checks.
  /** The client's own reference for the job — for Dell the dispatch number (SN `serviceContext.correlation_id`). */
  @Field(() => String)
  correlationId: Maybe<string>;

  @Field(() => String)
  clientSysId: Maybe<string>;

  @Field(() => String)
  clientName: Maybe<string>;

  @Field(() => String)
  clientCode: Maybe<string>;

  @Field(() => String)
  contactName: Maybe<string>;

  @Field(() => String)
  contactPhone: Maybe<string>;

  @Field(() => String)
  contactEmail: Maybe<string>;

  @Field(() => String)
  pendingAction: Maybe<string>;

  /**
   * "Report a problem" buttons enabled (opt-in) for the ticket's customer (ProblemReportKeys,
   * from SmartTeamsCustomer.enabledProblemReports). Populated by getTicketByNumber; empty when
   * the client has no SmartTeamsCustomer record or hasn't opted any buttons in.
   */
  @Field(() => [String], { default: v => v ?? [] })
  enabledProblemReports: Maybe<string[]>;

  // PUDO (pickup/drop-off) depot where the FE collects/returns parts. Mapped from the SN
  // `pudo_location` object the same way as the site `location` above. ticket_details only.
  @Field(() => String)
  pudoLocationLabel: Maybe<string>;

  @Field(() => Float)
  pudoLocationLat: Maybe<number>;

  @Field(() => Float)
  pudoLocationLng: Maybe<number>;

  /** Part line items on this ticket (ticket_details only; empty array when none/on list view). */
  @Field(() => [ServiceNowTicketPart], { default: v => v ?? [] })
  parts: Maybe<ServiceNowTicketPart[]>;

  // ── Colleague-view fields ────────────────────────────────────────────────────────────────
  // Populated by the colleague queries (api/colleague-ticket-queries.ts). The self-ticket
  // queries leave them empty.

  /**
   * Scheduling window, as SN's raw `yyyy-mm-dd hh:mm:ss`. This — not `expectedStart` — is the
   * field that actually carries a date: every assigned ticket sampled on nsctest had a window and
   * none had an `expected_start`.
   */
  @Field(() => String)
  windowStart: Maybe<string>;

  @Field(() => String)
  windowEnd: Maybe<string>;

  /** SN `fulfilment_status`, e.g. `Parts available` / `Parts at PUDO` / `Labor only`. */
  @Field(() => String)
  fulfilmentStatusLabel: Maybe<string>;

  /** Work notes, newest first, each with its timestamp and sanitized body. */
  @Field(() => [ServiceNowTicketNote], { default: v => v ?? [] })
  workNotes: Maybe<ServiceNowTicketNote[]>;

  /**
   * Dell reply code (CAD / CNL3 / PND …) for the flag icon.
   *
   * Holds the **display** half of NSC's pair list (CNL3, not S03) — see `_dell-reply-codes.ts`,
   * which resolves whichever half ServiceNow happens to store.
   *
   * Sourced from `SNSmartTeamTicket.u_replycode`, which ServiceNow began returning on 2026-09-18.
   * Null whenever that field is blank, which is still every ticket on nsctest — the UI shows a
   * "TBA" placeholder in that case.
   */
  @Field(() => String)
  replyCode: Maybe<string>;

  /**
   * Plain-English meaning of {@link replyCode}, e.g. "Service Cancelled before Engineer Onsite".
   * Empty when the stored value isn't one of NSC's 16 authoritative pairs.
   */
  @Field(() => String)
  replyCodeMeaning: Maybe<string>;

  /**
   * ServiceNow's own composite label, verbatim — `PND - Parts not dispatched [P05]`.
   *
   * Kept alongside the resolved code because it is the fullest description available and is what
   * the UI shows on hover; unlike {@link replyCodeMeaning} it is present even for codes outside
   * NSC's 16 pairs (`CCL - Cancellation Requested [CCL]`).
   */
  @Field(() => String)
  replyCodeDisplay: Maybe<string>;

  /**
   * Parts status shown by the puzzle icon — "not found", or the name of the FE currently holding
   * the parts.
   *
   * Always null today, for the same reason as {@link replyCode}: SN's `part_requirements[].stage`
   * only ever returns `located` / `at_pudo` / `shipment_preparation` / `in_transit`, with nothing
   * expressing "not found" and nothing naming another engineer.
   */
  @Field(() => String)
  partsStatusLabel: Maybe<string>;
}
